import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, User, AlertCircle, CheckCircle, XCircle, RefreshCw, List, CalendarDays, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import moment from "moment";
import { getMyCallbacks, completeCallback, getCallbackStats } from "../api/callbackApi";
import { VoiceCallModal } from "../components/leads/VoiceCallModal";
import { CallbackCalendar } from "../components/callbacks/CallbackCalendar";
import { useAuth } from "../hooks/useAuth";

export function MyCallbacks() {
    const { user } = useAuth();
    const [callbacks, setCallbacks] = useState([]);
    const [filteredCallbacks, setFilteredCallbacks] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [viewMode, setViewMode] = useState("list"); // list or calendar

    useEffect(() => {
        fetchCallbacks();
        fetchStats();
        
        // Auto-refresh every minute
        const interval = setInterval(() => {
            fetchCallbacks();
            fetchStats();
        }, 60000);
        
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        applyFilter();
    }, [filter, callbacks]);

    const fetchCallbacks = async () => {
        try {
            const response = await getMyCallbacks();
            if (response.status === 200) {
                setCallbacks(response.data);
            }
        } catch (error) {
            toast.error("Failed to load callbacks");
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await getCallbackStats();
            if (response.status === 200) {
                setStats(response.data);
            }
        } catch (error) {
            // Silent error
        }
    };

    const applyFilter = () => {
        const now = moment();
        const today = moment().startOf('day');
        const tomorrow = moment().add(1, 'day').startOf('day');
        const weekEnd = moment().add(7, 'days').endOf('day');

        let filtered = [...callbacks];

        switch (filter) {
            case "overdue":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isBefore(now)
                );
                break;
            case "today":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isBetween(today, tomorrow, null, '[)')
                );
                break;
            case "tomorrow":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isBetween(tomorrow, moment(tomorrow).add(1, 'day'), null, '[)')
                );
                break;
            case "week":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isBetween(now, weekEnd, null, '[]')
                );
                break;
            default:
                // all - no filter
                break;
        }

        // Sort by scheduled time
        filtered.sort((a, b) => 
            moment(a.callbackSchedule.scheduledFor).diff(moment(b.callbackSchedule.scheduledFor))
        );

        setFilteredCallbacks(filtered);
    };

    const handleCallNow = (lead) => {
        setSelectedLead(lead);
        setShowLeadModal(true);
    };

    const handleLeadModalClose = () => {
        setShowLeadModal(false);
        setSelectedLead(null);
        fetchCallbacks();
        fetchStats();
    };

    const getCallbackStatus = (scheduledFor) => {
        const now = moment();
        const scheduled = moment(scheduledFor);
        
        if (scheduled.isBefore(now)) {
            return { label: "OVERDUE", color: "red", bgColor: "bg-red-50", borderColor: "border-red-200" };
        } else if (scheduled.isBefore(moment().endOf('day'))) {
            return { label: "TODAY", color: "orange", bgColor: "bg-orange-50", borderColor: "border-orange-200" };
        } else if (scheduled.isBefore(moment().add(1, 'day').endOf('day'))) {
            return { label: "TOMORROW", color: "yellow", bgColor: "bg-yellow-50", borderColor: "border-yellow-200" };
        } else {
            return { label: "UPCOMING", color: "green", bgColor: "bg-green-50", borderColor: "border-green-200" };
        }
    };

    const getTimeUntil = (scheduledFor) => {
        const now = moment();
        const scheduled = moment(scheduledFor);
        const duration = moment.duration(scheduled.diff(now));

        if (duration.asSeconds() < 0) {
            const overdue = moment.duration(now.diff(scheduled));
            const hours = Math.floor(overdue.asHours());
            const minutes = overdue.minutes();
            return hours > 0 ? `${hours}h ${minutes}m overdue` : `${minutes}m overdue`;
        }

        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        if (days > 0) return `in ${days}d ${hours}h`;
        if (hours > 0) return `in ${hours}h ${minutes}m`;
        return `in ${minutes}m`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <Calendar className="h-8 w-8 text-blue-600" />
                        My Scheduled Callbacks
                    </h1>
                    <p className="text-gray-600 mt-1">Manage your upcoming callbacks and follow-ups</p>
                </div>
                <Button onClick={fetchCallbacks} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total</p>
                                    <p className="text-2xl font-bold">{stats.pending}</p>
                                </div>
                                <Calendar className="h-8 w-8 text-blue-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Due Today</p>
                                    <p className="text-2xl font-bold text-orange-600">{stats.dueToday}</p>
                                </div>
                                <Clock className="h-8 w-8 text-orange-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Overdue</p>
                                    <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                                </div>
                                <AlertCircle className="h-8 w-8 text-red-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">This Week</p>
                                    <p className="text-2xl font-bold text-green-600">{stats.dueThisWeek}</p>
                                </div>
                                <CalendarDays className="h-8 w-8 text-green-600" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* View Mode Toggle */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex items-center justify-center">
                        <div className="flex items-center gap-2 border rounded-lg p-1">
                            <Button
                                variant={viewMode === "list" ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setViewMode("list")}
                            >
                                <List className="h-4 w-4 mr-1" />
                                List
                            </Button>
                            <Button
                                variant={viewMode === "calendar" ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setViewMode("calendar")}
                            >
                                <CalendarDays className="h-4 w-4 mr-1" />
                                Calendar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Calendar View */}
            {viewMode === "calendar" && (
                <CallbackCalendar 
                    callbacks={filteredCallbacks}
                    onCallbackClick={(callback) => {
                        setSelectedLead(callback);
                        setShowLeadModal(true);
                    }}
                />
            )}

            {/* List View */}
            {viewMode === "list" && (
                <div className="space-y-4">
                    {filteredCallbacks.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {filter === "all" ? "No Callbacks Scheduled" : `No ${filter.charAt(0).toUpperCase() + filter.slice(1)} Callbacks`}
                            </h3>
                            <p className="text-gray-600">
                                {filter === "all" 
                                    ? "You don't have any scheduled callbacks at the moment."
                                    : `You don't have any ${filter} callbacks.`
                                }
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    filteredCallbacks.map((callback) => {
                        const status = getCallbackStatus(callback.callbackSchedule.scheduledFor);
                        return (
                            <Card key={callback._id} className={`${status.bgColor} border ${status.borderColor}`}>
                                <CardContent className="py-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h3 className="text-lg font-semibold">
                                                    {callback.firstName} {callback.lastName}
                                                </h3>
                                                <Badge className={`bg-${status.color}-100 text-${status.color}-800 border-${status.color}-200`}>
                                                    {status.label}
                                                </Badge>
                                            </div>
                                            <div className="space-y-1 text-sm text-gray-700">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4" />
                                                    <span>
                                                        {moment(callback.callbackSchedule.scheduledFor).format("MMM D, YYYY h:mm A")}
                                                        <span className="ml-2 font-medium">({getTimeUntil(callback.callbackSchedule.scheduledFor)})</span>
                                                    </span>
                                                </div>
                                                {callback.callbackSchedule.note && (
                                                    <div className="flex items-start gap-2">
                                                        <User className="h-4 w-4 mt-0.5" />
                                                        <span className="italic">{callback.callbackSchedule.note}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <Button
                                                onClick={() => handleCallNow(callback)}
                                                className=""
                                            >
                                                <PhoneCall className="h-4 w-4 mr-2" />
                                                Call Now
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
                </div>
            )}

            {/* Lead Modal */}
            {selectedLead && (
                <VoiceCallModal
                    open={showLeadModal}
                    onClose={handleLeadModalClose}
                    lead={selectedLead}
                    onSuccess={handleLeadModalClose}
                    onLeadUpdate={(updatedLead) => {
                        setCallbacks(prevCallbacks =>
                            prevCallbacks.map(cb => cb._id === updatedLead._id ? updatedLead : cb)
                        );
                    }}
                />
            )}
        </div>
    );
}

